import { Group, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { tKeys, useTranslation } from 'locales';
import type { Sync, SyncState } from 'shared/hooks/use-load-syncs';

import { SyncDot } from './SyncDot';
import { STATE_BACKGROUNDS, STATE_BORDERS, STATE_TEXT_COLORS } from './SyncPill';

const STATE_SEVERITY: Record<SyncState, number> = {
  healthy: 0,
  running: 1,
  never_synced: 2,
  failing: 3,
};

const worstState = (syncs: Sync[]): SyncState =>
  syncs.reduce<SyncState>(
    (worst, sync) => (STATE_SEVERITY[sync.state] > STATE_SEVERITY[worst] ? sync.state : worst),
    'healthy',
  );

type SyncSummaryPillProps = {
  syncs: Sync[];
};

export const SyncSummaryPill = ({ syncs }: SyncSummaryPillProps) => {
  const { t } = useTranslation();
  const state = worstState(syncs);
  const failingCount = syncs.filter(sync => sync.state === 'failing').length;

  return (
    <Group
      bg={STATE_BACKGROUNDS[state]}
      bd={`1px solid ${STATE_BORDERS[state]}`}
      justify="space-between"
      wrap="nowrap"
      gap="space2"
      px="space3"
      py="space2"
      style={{ borderRadius: 8 }}
    >
      <Group gap="space2" wrap="nowrap" style={{ minWidth: 0 }}>
        <Group gap={0} wrap="nowrap">
          {syncs.map(sync => (
            <SyncDot key={`${sync.kind}-${sync.integration}`} state={sync.state} />
          ))}
        </Group>
        <Text fw={600} fz="sm" c={STATE_TEXT_COLORS[state]} truncate>
          {t(tKeys.NAV.SYNC.INTEGRATIONS)}
        </Text>
      </Group>
      <Group gap="space2" wrap="nowrap" style={{ flexShrink: 0 }}>
        {failingCount > 0 && (
          <Text fz="xs" c={STATE_TEXT_COLORS[state]} style={{ whiteSpace: 'nowrap' }}>
            {t(failingCount === 1 ? tKeys.NAV.SYNC.FAILURE : tKeys.NAV.SYNC.FAILURES, {
              count: failingCount,
            })}
          </Text>
        )}
        <Text c={STATE_TEXT_COLORS[state]} style={{ display: 'flex' }}>
          <IconChevronRight size={16} />
        </Text>
      </Group>
    </Group>
  );
};
